import React, { createContext, useState, useContext, ReactNode, useCallback, useRef, useEffect } from 'react';

type CategorySidebarContextType = {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  closeWithDelay: (delayMs?: number) => void;
  cancelCloseDelay: () => void;
};

const CategorySidebarContext = createContext<CategorySidebarContextType | undefined>(undefined);

export const CategorySidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    clearTimer();
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'category' } }));
  }, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    setIsOpen(false);
  }, [clearTimer]);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  const closeWithDelay = useCallback((delayMs: number = 1000) => {
    clearTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, delayMs);
  }, [clearTimer]);

  const cancelCloseDelay = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  // Handle other sidebars opening
  useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'category') {
        clearTimer();
        setIsOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, [clearTimer]);

  // Support for closing nested components if needed
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return (
    <CategorySidebarContext.Provider 
      value={{ isOpen, toggle, open, close, closeWithDelay, cancelCloseDelay }}
    >
      {children}
    </CategorySidebarContext.Provider>
  );
};

export const useCategorySidebar = () => {
  const context = useContext(CategorySidebarContext);
  if (context === undefined) {
    throw new Error('useCategorySidebar must be used within a CategorySidebarProvider');
  }
  return context;
};
